import { Component, Input, Injector, OnInit, OnDestroy, ChangeDetectorRef, NgZone, EventEmitter, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { FormStateMatcher } from 'app/core/directives/form-validate';
import { MapService } from 'app/core/http/services/map.service';
import { ViaCepService } from 'app/core/http/services/viacep.service';
import { locationI, Location } from 'app/core/interfaces/location';
import { Address } from 'app/core/interfaces/adress';
import { OrganizationService } from '../../organization.service';
import { OrganizationLocationService } from 'app/core/http/services/organization-location.service';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { OrganizationInterface } from 'app/core/interfaces/organization';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'organization-location',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss'],
})
export class LocationComponent extends BaseComponent implements OnInit, OnDestroy {
  public locationForm!: FormGroup;

  @Input() address: locationI = <locationI>{};
  @Input() mode = 'CREATE';

  @Input() location!: Location;
  @Input() organization: OrganizationInterface = <OrganizationInterface>{};

  readonly mapStateMatcher = new FormStateMatcher('');
  public url = '';
  public zoom = 14;
  public latLong: [number, number] = [0, 0];

  public hasLocation = false;

  public submittedCep = false;
  public cepError = false;

  private readonly destroy$ = new Subject<void>();
  private lastSearchedCep = '';

  @Output() onUpdateLocation = new EventEmitter<Location>();

  constructor(
    public injector: Injector,
    private fb: FormBuilder,
    private map: MapService,
    private organizationService: OrganizationService,
    private organizationLocationService: OrganizationLocationService,
    private viaCepService: ViaCepService,
    private domSanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {
    super(injector);

    this.locationForm = this.fb.group({
      phone: new FormControl(null, [Validators.required, Validators.pattern(/^(\d{10}|\d{11})$/)]),
      email: new FormControl(null, [Validators.nullValidator, Validators.email]),
      specialty: new FormControl(null, [Validators.required, Validators.pattern(/^[a-zA-ZÀ-ÿ\s]+$/)]),
      mapUrl: new FormControl('', [Validators.nullValidator]),
      address: this.fb.group({
        cep: new FormControl(null, [Validators.required, Validators.pattern(/^\d{5}-?\d{3}$/)]),
        number: new FormControl(null, [Validators.required]),
        country: new FormControl(null, [Validators.required]),
        city: new FormControl(null, [Validators.required]),
        state: new FormControl(null, [Validators.required]),
        street: new FormControl(null, [Validators.required]),
        district: new FormControl(null, [Validators.nullValidator]),
        complement: new FormControl(null, [Validators.nullValidator]),
      }),
    });
  }

  ngOnInit(): void {
    this.setupAddressAutomation();
    this.getLocation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupAddressAutomation(): void {
    const addressForm = this.locationForm.get('address');
    const cepControl = addressForm?.get('cep');

    cepControl?.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((cep: string) => {
        const normalizedCep = this.layout.getOnlyNumbers(cep || '').toString();

        if (normalizedCep.length !== 8) {
          this.lastSearchedCep = '';
          this.cepError = false;
          return;
        }

        if (!cepControl.invalid && normalizedCep !== this.lastSearchedCep && !this.submittedCep) {
          this.searchAddressByCep();
        }
      });

    addressForm?.valueChanges
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe((address: Address) => this.updateGoogleMapsUrl(address));
  }

  getLocation() {
    this.loading = true;
    const organization_id = this.getUserLogged().id;
    this.organizationLocationService.showByOrganization(organization_id).subscribe({
      next: (organizationLocation: Location) => {
        this.location = organizationLocation;
        this.setValueToLocationForm(organizationLocation);
        this.emitLocationEvent(organizationLocation);

        this.loading = false;
        this.hasLocation = true;
      },
      error: (error: HttpErrorResponse) => {
        const erroType = error?.error?.error;

        if (error.status == 404 && erroType == 'Not Found') {
          this.checkLocationPermissionAndGetPosition();

          this.showWarningMessage = true;
          this.messageWarning = 'A visibilidade da organização no sistema depende do cadastro da localizacão';
          this.onUpdateLocation.emit(<Location>{});
        }

        this.loading = false;
      },
    });
  }

  emitLocationEvent(location: Location) {
    this.onUpdateLocation.emit(location);
  }

  setValueToLocationForm(location: Location): void {
    const address: Address = location.address;

    let formattedPhone = location.phone || '';
    if (formattedPhone.startsWith('55')) {
      formattedPhone = formattedPhone.replace('55', '');
    }

    this.locationForm.patchValue({
      address: {
        cep: address?.cep || null,
        number: address?.number || null,
        country: address?.country || null,
        city: address?.city || null,
        state: address?.state || null,
        street: address?.street || null,
        district: address?.district || null,
        complement: address?.complement || null,
      },
      mapUrl: location.mapUrl || '',
      phone: formattedPhone || null,
      mobile: location.mobile || null,
      email: location.email || null,
      specialty: location.specialty || null,
    }, { emitEvent: false });

    if (Number(location.position.latitude) != 0 && Number(location.position.longitude) != 0) {
      this.latLong = [location.position.latitude!, location.position.longitude!];
    } else {
      this.checkLocationPermissionAndGetPosition();
    }
  }

  onPasteName(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData?.getData('text') ?? '';
    const validNameRegex = /^[A-Za-zÀ-Üà-ü\s]+$/;

    if (!validNameRegex.test(clipboardData)) {
      event.preventDefault();
    }
  }

  private checkLocationPermissionAndGetPosition() {
    this.showErrorMessage = false;
    navigator.permissions
      .query({ name: 'geolocation' })
      .then((permissionStatus) => {
        if (permissionStatus.state === 'granted') {
          this.getCurrentPosition();
        } else if (permissionStatus.state === 'prompt') {
          this.requestLocation();
        } else {
          this.messageError = 'Permissão de localização negada. Por favor, habilite a localização nas configurações do navegador.';
          this.showErrorMessage = true;
          console.warn('Permissão de localização negada. Por favor, habilite a localização nas configurações do navegador.');
        }
      })
      .catch((error) => {
        console.error('Erro ao verificar permissão de localização:', error);
      });
  }

  private requestLocation() {
    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        this.zone.run(() => {
          if (!this.location?.position) this.latLong = [position.coords.latitude, position.coords.longitude];
          this.cdr.detectChanges();
        });
      },
      (error: GeolocationPositionError) => {
        console.error('Erro ao obter localização (usuário pode ter negado permissão):', error.message);
      },
    );
  }

  private getCurrentPosition() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position: any) => {
        this.zone.run(() => {
          this.latLong = [position.coords.latitude, position.coords.longitude];
        });

        this.cdr.detectChanges();
      });
    } else {
      console.error('falha ao buscar location');
    }
  }

  onLatLongChange(newLatLong: [number, number]) {
    //[longitude, latitude]
    this.latLong = newLatLong;
  }

  buscarCEP() {
    this.dialog
      .openSearchCepDialog({
        param: {},
      })
      .subscribe((address: any) => {
        if (address.cep) {
          this.locationForm.patchValue({
            address: {
              cep: address?.cep || null,
              number: address?.number || null,
              country: 'Brasil',
              city: address?.localidade || null,
              state: address?.uf || null,
              street: address?.logradouro || null,
              district: address?.bairro || null,
              // O ViaCEP usa "complemento" também para informar faixas de
              // numeração do CEP, não o complemento real do imóvel.
              complement: null,
            },
          });
        }
      });
  }

  searchAddressByCep() {
    this.submittedCep = true;
    this.cepError = false;

    const cep = this.layout.getOnlyNumbers(this.locationForm.value.address.cep || '').toString();
    this.lastSearchedCep = cep;

    this.viaCepService.findByCep(cep).subscribe({
      next: (address: any) => {
        const addressForm = this.locationForm.get('address');
        addressForm?.markAllAsTouched();

        if (!address.cep) {
          this.cepError = true;
          this.submittedCep = false;
          return;
        }

        addressForm?.patchValue({
          cep: address?.cep || null,
          number: address?.number || null,
          country: 'Brasil',
          city: address?.localidade || null,
          state: address?.uf || null,
          street: address?.logradouro || null,
          district: address?.bairro || null,
          // Não copiar a faixa de numeração retornada pelo ViaCEP para o
          // complemento informado pela organização.
          complement: null,
        }, { emitEvent: false });

        this.updateGoogleMapsUrl(addressForm?.value);
        this.requestPreciseBrowserLocation();

        this.submittedCep = false;
        this.cepError = false;
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.submittedCep = false;
        this.cepError = true;
      },
    });
  }

  setJsonAdress() {
    // Adicione a lógica para definir o endereço em formato JSON conforme necessário
  }

  create() {
    this.showErrorMessage = false;
    this.showSuccessMessage = false;
    this.loading = true;

    const hours = this.location?.hoursOfOperation;
    const hoursOfOperation = hours && hours.length > 0 ? hours : this.generateDefaultOperationHours();

    const locationFormValue = {
      ...this.location,
      organization_id: this.getUserLogged().id,
      organization_name: this.getUserLogged().name,
      ...this.locationForm.value,
      position: {
        latitude: String(this.latLong[0]),
        longitude: String(this.latLong[1]),
      },
      hoursOfOperation: hoursOfOperation,
    };

    locationFormValue.phone = `55${locationFormValue.phone}`;

    this.organizationLocationService.createNew(locationFormValue).subscribe({
      next: (location: Location) => {
        this.location = location;

        this.showErrorMessage = false;
        this.showSuccessMessage = true;

        this.showWarningMessage = false;
        this.loading = false;

        this.emitLocationEvent(location);

        if (!this.hasLocation) {
          this.openSnackBar('Localização criada com sucesso', 'success');

          setTimeout(() => {
            this.router.navigate(['organization-new/meus-dados'], { queryParams: { tab: 'horario' } });
          }, 500);

          this.hasLocation = true;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.showErrorMessage = true;
        this.showSuccessMessage = false;
        this.loading = false;

        console.log('location status code', err);

        if (err.status == 500) {
          this.messageError = 'Erro interno ao salvar a localização. Por favor, tente novamente em alguns instantes.';
        }

        if (err.status == 404) {
          this.messageError = 'Não foi possível Salvar a localizacação. Verifique os dados e tente novamente.';
          this.onUpdateLocation.emit(<Location>{});
        }
      },
    });
  }

  generateDefaultOperationHours() {
    const hours = [];
    for (let i = 1; i < 6; i++) {
      hours.push({
        dayOfWeek: i,
        timeStart: '07:00',
        timeEnd: '12:00',
      });

      hours.push({
        dayOfWeek: i,
        timeStart: '14:00',
        timeEnd: '18:00',
      });
    }

    return hours;
  }

  loadaddress(cep: string) {
    if (cep.length == 8) {
      const values = this.map.getLatLong(cep);
    }
  }

  getUrl() {
    this.url = `https://maps.google.com/maps?q=${this.address.latitude},${this.address.longitude}&hl=es&z=${this.zoom}&output=embed`;
    return this.domSanitizer.bypassSecurityTrustResourceUrl(this.url);
  }

  onChangeForm(event: any) {
    this.address.cep = this.layout.getOnlyNumbers(this.locationForm.get('cep')?.value).toString();
    this.loadaddress(this.address.cep.toString());
    // this.onUpdate.emit(this.address);
  }

  private updateGoogleMapsUrl(address: Partial<Address>): void {
    const parts = [address.street, address.number, address.district, address.city, address.state, address.country]
      .map((part) => String(part || '').trim())
      .filter(Boolean);

    if (parts.length < 4) return;

    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
    this.locationForm.get('mapUrl')?.setValue(mapUrl, { emitEvent: false });
  }

  private requestPreciseBrowserLocation(): void {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        this.zone.run(() => {
          this.latLong = [position.coords.latitude, position.coords.longitude];
          this.cdr.detectChanges();
        });
      },
      () => {
        // Sem permissão ou sem sinal, mantém a posição existente para permitir
        // o ajuste manual pelo marcador do mapa.
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }

  markAsTouched() {
    for (var i in this.locationForm.controls) {
      this.locationForm.controls[i].markAsTouched();
    }
  }
}
